import { motion } from "framer-motion";
import { Button } from "@/components/ui/button";
import { Clock, TrendingUp, Zap, ExternalLink } from "lucide-react";
import InteractiveGridBackground from "./InteractiveGridBackground";

import kanbanEventos from "@/assets/kanban-eventos.webp";
interface SupplierHeroProps {
  onCadastrarClick?: () => void;
  onQuizClick?: () => void;
}
const SupplierHero = ({
  onCadastrarClick,
  onQuizClick
}: SupplierHeroProps) => {
  const containerVariants = {
    hidden: {
      opacity: 0
    },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.15
      }
    }
  };
  const itemVariants = {
    hidden: {
      opacity: 0,
      y: 20
    },
    visible: {
      opacity: 1,
      y: 0
    }
  };
  return <section className="relative min-h-[70vh] flex items-center justify-center overflow-hidden bg-white">
      <InteractiveGridBackground cellSize={60} activeColor="rgba(155, 135, 245, 0.06)" borderColor="rgba(148, 163, 184, 0.04)" />



      

      <div className="relative z-10 container mx-auto px-4 py-20">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
          <motion.div className="text-left" variants={containerVariants} initial="hidden" animate="visible">
            <motion.div className="mb-6" variants={itemVariants}>


              <h1 className="text-4xl md:text-5xl lg:text-6xl font-normal font-heading text-brand-dark leading-tight tracking-tight">
                Plataforma feita para o seu Buffet acelerar as vendas e simplificar a gestão do evento
              </h1>
            </motion.div>

            <motion.p className="text-lg md:text-xl text-muted-foreground max-w-xl mb-6 font-sans" variants={itemVariants}>
              Leads, propostas, CRM e gestão financeira do seu buffet em um só lugar sem você precisar digitar nada
            </motion.p>

            <motion.div className="flex flex-col sm:flex-row gap-3 items-start sm:items-center mt-8" variants={itemVariants}>
              <Button asChild size="default" variant="outline" className="rounded-full px-6 border-brand-purple text-brand-purple hover:bg-brand-purple/10 text-sm w-auto">
                <a href="https://ameup.com.br/exemplo-cardapio-digital" target="_blank" rel="noopener noreferrer">
                  Exemplo ao vivo
                  <ExternalLink className="ml-2 h-4 w-4" />
                </a>
              </Button>
              <Button asChild size="default" className="bg-gradient-to-r from-[#FF5E0E] to-[#7C3AED] hover:opacity-90 text-white rounded-full px-6 shadow-lg shadow-[#FF5E0E]/25 text-sm w-auto border-0">
                <a href="/cadastro-fornecedor">Agendar uma demonstração</a>
              </Button>
            </motion.div>
          </motion.div>

          {/* Right: Computer + iPhone mockups */}
          <motion.div
            className="relative flex items-center justify-center min-h-[400px] lg:min-h-[500px]"
            initial={{ opacity: 0, scale: 0.9 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ duration: 0.6, delay: 0.3 }}
          >
            {/* Laptop mockup */}
            <div className="relative w-full max-w-[520px]">
              <div className="bg-zinc-900 rounded-t-xl p-2 pb-1 shadow-2xl">
                <div className="bg-white rounded-t-md overflow-hidden aspect-[16/10] flex items-center justify-center">
                  <img
                    src={kanbanEventos}
                    alt="Gestão de eventos no computador"
                    className="w-full h-full object-contain object-top"
                    width={520}
                    height={325}
                    loading="eager"
                    data-fetch-priority="high"
                    decoding="async"
                  />
                </div>
              </div>
              <div className="bg-zinc-800 h-3 rounded-b-2xl mx-[-12px] shadow-xl" />
              <div className="bg-zinc-700 h-1 w-24 mx-auto rounded-b-md" />
            </div>

            {/* iPhone mockup - Cardápio digital */}
            <div className="absolute -bottom-4 -right-2 sm:right-0 lg:-right-4 w-[140px] sm:w-[170px] lg:w-[200px]">
              <div className="bg-zinc-900 rounded-[2rem] p-2 shadow-2xl border border-zinc-700">
                <div className="bg-zinc-800 rounded-[1.6rem] overflow-hidden aspect-[9/19] relative">
                  <div className="absolute top-2 left-1/2 -translate-x-1/2 w-16 h-4 bg-zinc-900 rounded-full z-10" />
                  <img
                    src="https://qfozcnxbtjjmidhjdepc.supabase.co/storage/v1/object/public/Photos_Home/Menu%20digital.png"
                    alt="Cardápio digital no iPhone"
                    className="w-full h-full object-cover object-top"
                    width={200}
                    height={422}
                    loading="eager"
                    decoding="async"
                  />
                </div>
              </div>
            </div>

            {/* Floating WhatsApp+IA badge */}
            <motion.div
              className="absolute bottom-2 left-2 sm:bottom-4 sm:left-auto sm:right-[160px] lg:right-[190px] z-20"
              initial={{ opacity: 0, y: -10, scale: 0.9 }}
              animate={{ opacity: 1, y: 0, scale: 1 }}
              transition={{ duration: 0.5, delay: 0.5 }}
            >
              <div className="flex items-center gap-2 rounded-full bg-white border border-slate-200 shadow-lg pl-2 pr-3 py-1.5">
                <div className="relative flex h-5 w-5 sm:h-6 sm:w-6 items-center justify-center rounded-full bg-[#25D366]">
                  <svg viewBox="0 0 32 32" className="h-3 w-3 sm:h-3.5 sm:w-3.5" fill="white" aria-hidden="true">
                    <path d="M16.003 3.2c-7.06 0-12.8 5.74-12.8 12.8 0 2.257.593 4.463 1.72 6.404L3.2 28.8l6.55-1.71a12.77 12.77 0 006.253 1.635h.005c7.058 0 12.798-5.74 12.801-12.8 0-3.42-1.33-6.635-3.75-9.056A12.72 12.72 0 0016.003 3.2zm0 2.4c2.775 0 5.383 1.08 7.343 3.04a10.32 10.32 0 013.055 7.36c-.002 5.735-4.665 10.4-10.4 10.4h-.004a10.36 10.36 0 01-5.28-1.445l-.378-.225-3.887 1.017 1.037-3.79-.246-.39a10.34 10.34 0 01-1.583-5.567c0-5.734 4.666-10.4 10.343-10.4zm-3.79 5.6c-.176 0-.462.066-.703.33-.24.264-.92.9-.92 2.196s.941 2.55 1.072 2.726c.132.176 1.828 2.928 4.517 3.998 2.234.882 2.69.706 3.175.662.484-.044 1.564-.638 1.784-1.254.22-.616.22-1.144.154-1.254-.066-.11-.242-.176-.506-.308-.264-.132-1.564-.77-1.806-.858-.242-.088-.418-.132-.594.132-.176.264-.68.858-.834 1.034-.154.176-.308.198-.572.066-.264-.132-1.115-.411-2.123-1.31-.785-.7-1.315-1.564-1.469-1.828-.154-.264-.017-.407.116-.539.119-.118.264-.308.396-.462.132-.154.176-.264.264-.44.088-.176.044-.33-.022-.462-.066-.132-.594-1.436-.814-1.964-.214-.516-.432-.446-.594-.454-.154-.007-.33-.009-.506-.009z"/>
                  </svg>
                  <span className="absolute -right-0.5 -top-0.5 flex h-2 w-2 sm:h-2.5 sm:w-2.5">
                    <span className="absolute inline-flex h-full w-full animate-ping rounded-full bg-[#25D366] opacity-75" />
                    <span className="relative inline-flex h-2 w-2 sm:h-2.5 sm:w-2.5 rounded-full bg-[#25D366] border-2 border-white" />
                  </span>
                </div>
                <div className="flex flex-col leading-tight">
                  <span className="text-[10px] sm:text-[11px] font-bold text-brand-dark">Integração com WhatsApp</span>
                  <span className="text-[8px] sm:text-[9px] text-muted-foreground">proposta enviada em 1 min</span>
                </div>
              </div>
            </motion.div>

            {/* Floating Google Calendar badge */}
            <motion.div
              className="absolute -top-2 left-2 sm:top-20 sm:-left-6 lg:-left-12 z-20 max-w-[70%] sm:max-w-none"
              initial={{ opacity: 0, x: -10, scale: 0.9 }}
              animate={{ opacity: 1, x: 0, scale: 1 }}
              transition={{ duration: 0.5, delay: 0.7 }}
            >
              <div className="flex items-center gap-2 rounded-full bg-white border border-slate-200 shadow-lg pl-2 pr-3 py-1.5">
                <div className="flex h-5 w-5 sm:h-6 sm:w-6 items-center justify-center rounded-full bg-white border border-slate-200">
                  <svg viewBox="0 0 48 48" className="h-3 w-3 sm:h-3.5 sm:w-3.5" aria-hidden="true">
                    <path fill="#EA4335" d="M24 9.5c3.54 0 6.71 1.22 9.21 3.6l6.85-6.85C35.9 2.38 30.47 0 24 0 14.62 0 6.51 5.38 2.56 13.22l7.98 6.19C12.43 13.72 17.74 9.5 24 9.5z"/>
                    <path fill="#4285F4" d="M46.98 24.55c0-1.57-.15-3.09-.38-4.55H24v9.02h12.94c-.58 2.96-2.26 5.48-4.78 7.18l7.73 6c4.51-4.18 7.09-10.36 7.09-17.65z"/>
                    <path fill="#FBBC05" d="M10.53 28.59c-.48-1.45-.76-2.99-.76-4.59s.27-3.14.76-4.59l-7.98-6.19C.92 16.46 0 20.12 0 24c0 3.88.92 7.54 2.56 10.78l7.97-6.19z"/>
                    <path fill="#34A853" d="M24 48c6.48 0 11.93-2.13 15.89-5.81l-7.73-6c-2.15 1.45-4.92 2.3-8.16 2.3-6.26 0-11.57-4.22-13.47-9.91l-7.98 6.19C6.51 42.62 14.62 48 24 48z"/>
                  </svg>
                </div>

                <div className="flex flex-col leading-tight">
                  <span className="text-[10px] sm:text-[11px] font-bold text-brand-dark">Sincronizado com Google Calendar</span>
                  <span className="text-[8px] sm:text-[9px] text-muted-foreground">agenda sempre atualizada</span>
                </div>
              </div>
            </motion.div>

            {/* Floating Claude AI badge */}
            <motion.div
              className="absolute top-8 right-2 sm:top-1 sm:right-20 lg:top-2 lg:right-28 z-20 max-w-[70%] sm:max-w-none"
              initial={{ opacity: 0, y: -10, scale: 0.9 }}
              animate={{ opacity: 1, y: 0, scale: 1 }}
              transition={{ duration: 0.5, delay: 0.9 }}
            >
              <div className="flex items-center gap-2 rounded-full bg-white border border-slate-200 shadow-lg pl-2 pr-3 py-1.5">
                <div className="flex h-5 w-5 sm:h-6 sm:w-6 items-center justify-center rounded-full bg-[#D97757]">
                  <svg viewBox="0 0 46 32" className="h-3 w-3 sm:h-3.5 sm:w-3.5" fill="white" aria-hidden="true">
                    <path d="M32.73 0h-6.945l12.665 32H45.4L32.73 0zM12.665 0L0 32h7.082l2.59-6.72h13.25L25.51 32h7.082L19.929 0h-7.264zm-.702 19.337l4.334-11.246 4.334 11.246h-8.668z"/>
                  </svg>
                </div>

                <div className="flex flex-col leading-tight">
                  <span className="text-[10px] sm:text-[11px] font-bold text-brand-dark">Dicas de vendas com a IA</span>
                  <span className="text-[8px] sm:text-[9px] text-muted-foreground">powered by Claude</span>
                </div>
              </div>
            </motion.div>



          </motion.div>
        </div>


        <motion.div
          className="mt-12 grid grid-cols-1 sm:grid-cols-3 gap-3 max-w-4xl mx-auto"
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6, delay: 0.6 }}
        >
          {[
            { icon: Clock, value: "-82%", label: "no tempo de envio de proposta" },
            { icon: TrendingUp, value: "+33%", label: "na conversão de vendas" },
            { icon: Zap, value: "1 min", label: "para enviar um proposta" },
          ].map(({ icon: Icon, value, label }) => (
            <div
              key={label}
              className="flex items-center gap-3 rounded-xl bg-white/80 backdrop-blur-sm border border-slate-200 px-4 py-3 shadow-sm"
            >
              <div className="shrink-0 w-9 h-9 rounded-lg bg-[#eefda3] flex items-center justify-center">
                <Icon className="w-4 h-4 text-brand-dark" strokeWidth={2.5} />
              </div>
              <div>
                <div className="text-lg font-extrabold font-heading text-brand-dark leading-none">
                  {value}
                </div>
                <div className="text-xs text-muted-foreground mt-1 leading-tight">{label}</div>
              </div>
            </div>
          ))}
        </motion.div>
      </div>
    </section>;
};
export default SupplierHero;